import {
  Outlet,
  createBrowserRouter,
  redirect,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import ErrorPage from "./error-page";
import App from "./App.tsx";
import LoginPage, { loginAction, logoutAction } from "./components/LoginPage";
import Table from "./components/Table";
import InteractiveForm, {
  loader as interactiveFormLoader,
  publicLoader as publicAlgorithmLoader,
} from "./components/InteractiveForm";
import AlgorithmBuilder, {
  loader as algorithmBuilderLoader,
} from "./components/AlgorithmBuilder";
import NavBar from "./components/NavBar";
import AlgorithmsPage, {
  loader as algorithmsPageLoader,
} from "./components/AlgorithmsPage";
import ProgressBar from "./components/ProgressBar";
import CrohnsForm, { loader as corhnsLoader } from "./components/CrohnsForm";
import GoogleAnalytics from "./components/GoogleAnalytics/GoogleAnalytics";
import RegistrationPage, {
  action as registerAction,
} from "./components/RegistrationPage/";
import EmailVerificationPage, {
  action as EmailVerificationAction,
} from "./components/EmailVerificationPage";
import { fetchUser, requireUser } from "./auth.tsx";

import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import AppVersionChecker from "./components/AppVersionChecker.tsx";
import ThemeSelector from "./components/ThemeSelector/ThemeSelector.tsx";
import ColonoscopyFlow from "./components/ColonoscopyFlow";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.PROD ? "prod" : "dev",
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  tracePropagationTargets: [
    "localhost",
    /^.*\.my-gut-guide.com/,
  ],
  replaysSessionSampleRate: 0.01,
  replaysOnErrorSampleRate: 1.0,
  profilesSampleRate: 0.1,
});

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);

export const router = sentryCreateBrowserRouter(
  [
    {
      path: "/",
      element: (
        <>
          <ProgressBar />
          <Outlet />
          <AppVersionChecker />
          <GoogleAnalytics />
        </>
      ),
      errorElement: <ErrorPage />,
      children: [
        // Auth routes
        {
          path: "auth",
          loader: async () => (await fetchUser()) && redirect("/"),
          children: [
            {
              path: "login",
              action: loginAction,
              element: <LoginPage />,
            },
            {
              path: "register",
              action: registerAction,
              element: <RegistrationPage />,
            },
            {
              path: "confirm-email/:confirmationToken",
              action: EmailVerificationAction,
              element: <EmailVerificationPage />,
            },
          ],
        },
        // Demo pages (Auth not required)
        {
          element: (
            <>
              <NavBar />
              <ThemeSelector>
                <div className="relative max-w-7xl mx-auto p-8 pb-0">
                  <Outlet />
                </div>
              </ThemeSelector>
            </>
          ),
          children: [
            {
              path: "form/h-pylori-algorithm",
              element: <InteractiveForm />,
              loader: interactiveFormLoader("h-pylori-algorithm"),
            },
            {
              path: "form/gastric-intestinal-metaplasia-algorithm",
              element: <InteractiveForm />,
              loader: interactiveFormLoader(
                "gastric-intestinal-metaplasia-algorithm"
              ),
            },
            {
              path: "form/pancreatic-cysts-algorithm",
              element: <InteractiveForm />,
              loader: interactiveFormLoader("pancreatic-cysts-algorithm"),
            },
            {
              path: "form/public/:algorithmId",
              element: <InteractiveForm />,
              loader: publicAlgorithmLoader,
            },
            {
              path: "table",
              element: <Table />,
            },
            {
              path: "crohns",
              element: <CrohnsForm />,
              loader: corhnsLoader,
            },
          ],
        },
        // Auth required
        {
          element: (
            <>
              <NavBar />
              <div className="relative max-w-7xl mx-auto p-8 pb-0">
                <Outlet />
              </div>
            </>
          ),
          errorElement: (
            <>
              <NavBar />
              <div className="relative max-w-7xl mx-auto p-8 pb-0">
                <ErrorPage />
              </div>
            </>
          ),
          loader: async () => await requireUser(),
          children: [
            {
              index: true,
              element: <App />,
            },
            {
              path: "form/:algorithmId",
              element: <InteractiveForm />,
              loader: interactiveFormLoader(),
            },
            {
              path: "algorithm-builder",
              element: <AlgorithmBuilder />,
              loader: algorithmBuilderLoader,
            },
            {
              path: "algorithms",
              element: <AlgorithmsPage />,
              loader: algorithmsPageLoader,
            },
            {
              path: "algorithm-builder/:algorithmId",
              element: <AlgorithmBuilder />,
              loader: algorithmBuilderLoader,
            },
            {
              path: "colonoscopy-flow",
              element: <ColonoscopyFlow />,
            },
            {
              path: "/logout",
              action: logoutAction,
            },
          ],
        },
      ],
    },
  ],
  { basename: import.meta.env.VITE_BASE_PREFIX || "" }
);
